<template>

    <div class="relative rounded-2xl bg-white/90 dark:bg-surface-900 shadow-lg shadow-black/[0.03] backdrop-blur-sm before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit] before:border before:border-transparent before:[background:linear-gradient(theme(colors.gray.100),theme(colors.gray.200))_border-box] before:[mask-composite:exclude_!important] before:[mask:linear-gradient(white_0_0)_padding-box,_linear-gradient(white_0_0)] dark:before:[background:linear-gradient(theme(colors.surface.700),theme(colors.surface.800))_border-box]">

        <!-- Intestazione: titolo, conteggio e ricerca -->
        <div class="relative flex flex-col gap-4 border-b border-surface-200 p-4 dark:border-surface-800 lg:flex-row lg:items-center lg:justify-between">

            <div class="flex items-center gap-3">

                <span class="material-symbols-outlined material-symbols-font-300 rounded-lg bg-primary/20 p-2 text-amber-700 dark:text-primary">
                    format_list_numbered
                </span>

                <div>
                    <h3 class="text-base font-bold leading-tight">Classifica generale</h3>
                    <p class="text-xs text-surface-500 dark:text-surface-400 tabular-nums">
                        <template v-if="loading && !hasData"> Caricamento… </template>
                        <template v-else-if="search"> {{ totalRecords }} {{ totalRecords === 1 ? 'squadra trovata' : 'squadre trovate' }} </template>
                        <template v-else> {{ totalRecords }} squadre in gara </template>
                    </p>
                </div>

            </div>

            <!-- Campo di ricerca -->
            <div class="relative w-full lg:max-w-xs">

                <span class="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 !text-lg text-surface-400">
                    search
                </span>

                <InputText
                    :modelValue="search"
                    @update:modelValue="$emit('update:search', $event)"
                    type="text"
                    aria-label="Cerca la tua squadra"
                    placeholder="Cerca la tua squadra..."
                    class="!pl-10 !pr-10"
                />

                <button
                    v-if="search"
                    type="button"
                    aria-label="Azzera la ricerca"
                    class="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-surface-400 transition-colors hover:bg-surface-100 hover:text-surface-700 dark:hover:bg-surface-800 dark:hover:text-surface-200"
                    @click="$emit('update:search', '')"
                >
                    <span class="material-symbols-outlined !text-lg"> close </span>
                </button>

            </div>

        </div>

        <!-- Skeleton al primo caricamento -->
        <div v-if="loading && !hasData" class="space-y-2 p-4">
            <Skeleton v-for="index in skeletonRows" :key="index" height="2.75rem" class="!rounded-lg" />
        </div>

        <!-- Stato vuoto -->
        <div v-else-if="!hasData" class="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center">
            <span class="material-symbols-outlined !text-4xl text-surface-300 dark:text-surface-600"> search_off </span>
            <p class="font-semibold">Nessuna squadra trovata</p>
            <p class="max-w-sm text-sm text-surface-500 dark:text-surface-400">
                Controlla il nome inserito: deve corrispondere a quello registrato su Leghe Fantacalcio.
            </p>
            <Button
                v-if="search"
                label="Azzera la ricerca"
                severity="contrast"
                outlined
                size="small"
                @click="$emit('update:search', '')"
            />
        </div>

        <!-- Tabella -->
        <div v-else class="relative">

            <!-- Velo durante i ricaricamenti, così la tabella non "sparisce" a ogni cambio pagina -->
            <div v-if="loading" class="absolute inset-0 z-20 flex items-start justify-center bg-white/50 pt-10 dark:bg-surface-900/50">
                <ProgressSpinner :strokeWidth="'5'" :animationDuration="'.6s'" aria-label="Caricamento" class="!h-8 !w-8" />
            </div>

            <div class="max-h-[32rem] overflow-auto">

                <table class="w-full border-collapse text-left">

                    <caption class="sr-only">Classifica generale del Fanta Blondin</caption>

                    <thead class="sticky top-0 z-10 bg-surface-50/95 backdrop-blur-sm dark:bg-surface-800/95">
                        <tr class="text-[0.7rem] uppercase tracking-widest text-surface-500 dark:text-surface-400">
                            <th scope="col" class="w-16 px-4 py-3 text-center font-bold sm:w-20"> Pos. </th>
                            <th scope="col" class="px-2 py-3 font-bold"> Squadra </th>
                            <th scope="col" class="w-24 px-4 py-3 text-right font-bold sm:w-32"> Punti </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr
                            v-for="team in teams"
                            :key="team.id ?? team.posizione"
                            class="border-t border-surface-100 transition-colors hover:bg-primary/10 dark:border-surface-800 dark:hover:bg-primary/10"
                            :class="isPodium(team.posizione) ? 'bg-primary/[0.06] dark:bg-primary/[0.04]' : ''"
                        >

                            <!-- Posizione -->
                            <td class="px-4 py-2.5">
                                <div
                                    class="mx-auto flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold tabular-nums ring-1"
                                    :class="positionStyle(team.posizione)"
                                >
                                    {{ team.posizione }}
                                </div>
                            </td>

                            <!-- Squadra -->
                            <td class="px-2 py-2.5">
                                <div class="flex items-center gap-2">
                                    <span class="font-semibold" :class="isPodium(team.posizione) ? 'text-surface-900 dark:text-white' : ''">
                                        {{ team.squadra }}
                                    </span>
                                    <span
                                        v-if="isPodium(team.posizione)"
                                        class="material-symbols-outlined material-symbols-fill-1 !text-base"
                                        :class="medalColor(team.posizione)"
                                        :aria-label="`${team.posizione}° posto`"
                                    >
                                        {{ team.posizione === 1 ? 'emoji_events' : 'military_tech' }}
                                    </span>
                                </div>
                            </td>

                            <!-- Punti -->
                            <td class="px-4 py-2.5 text-right font-bold tabular-nums">
                                {{ formatPoints(team.punti_totali) }}
                            </td>

                        </tr>
                    </tbody>

                </table>

            </div>

            <!-- Paginazione -->
            <Paginator
                v-if="totalRecords > rows"
                :first="first"
                :rows="rows"
                :totalRecords="totalRecords"
                :rowsPerPageOptions="isMobile ? null : rowsPerPageOptions"
                :template="paginatorTemplate"
                currentPageReportTemplate="{first} - {last} di {totalRecords}"
                class="!rounded-b-2xl !border-t !border-surface-200 !bg-transparent dark:!border-surface-800"
                @page="$emit('page', $event)"
            />

        </div>

    </div>

</template>

<script setup>

    // based imports
    import { computed } from 'vue';

    // services imports
    import { isMobile } from 'mobile-device-detect';
    import { formatPoints } from '@/helpers/number';

    // definisco le props
    const props = defineProps({
        teams: {
            type: Array,
            default: () => []
            // Righe della pagina corrente: [{ id, posizione, squadra, punti_totali }]
        },
        totalRecords: {
            type: Number,
            default: 0
        },
        first: {
            type: Number,
            default: 0
        },
        rows: {
            type: Number,
            default: 20
        },
        search: {
            type: String,
            default: ''
        },
        loading: {
            type: Boolean,
            default: false
        }
    });

    // definisco gli eventi
    defineEmits(['update:search', 'page']);

    // opzioni di paginazione
    const rowsPerPageOptions = [20, 50, 100];

    // su mobile riduco i controlli del paginatore per non mandarlo a capo
    const paginatorTemplate = computed(() => isMobile
        ? 'PrevPageLink CurrentPageReport NextPageLink'
        : 'RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink'
    );

    // numero di skeleton coerente con la dimensione della pagina
    const skeletonRows = computed(() => Math.min(props.rows, 10));

    // indica se ci sono dati da mostrare
    const hasData = computed(() => Array.isArray(props.teams) && props.teams.length > 0);

    // le prime tre posizioni mantengono i colori del podio anche in tabella
    const isPodium = (position) => [1, 2, 3].includes(parseInt(position));

    const positionStyle = (position) => {
        switch (parseInt(position)) {
            case 1:
                return 'bg-primary text-secondary ring-primary';
            case 2:
                return 'bg-zinc-300 text-zinc-700 ring-zinc-300';
            case 3:
                return 'bg-amber-600 text-white ring-amber-600';
            default:
                return 'bg-surface-100 text-surface-600 ring-surface-200 dark:bg-surface-800 dark:text-surface-300 dark:ring-surface-700';
        }
    };

    const medalColor = (position) => {
        switch (parseInt(position)) {
            case 1:
                return 'text-amber-500';
            case 2:
                return 'text-zinc-400';
            default:
                return 'text-amber-600';
        }
    };

</script>
